import React, { useEffect, useRef } from "react";
import "./MiddleBanner.css";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import flipcar from "./assets/rotateCar.png";
import carLightsOn from "./assets/carLightsOn.png"; // New image for lights on

// Ensure GSAP plugins are registered
gsap.registerPlugin(ScrollTrigger);

const MiddleBanner = () => {
    // Create refs for all animated elements
    const containerRef = useRef(null);
    const carRef = useRef(null);
    const lightsRef = useRef(null); // New ref for lights image
    const overlayRef = useRef(null); // New ref for background overlay
    const textRefs = useRef([]);

    // Store text refs in array
    const addToRefs = (el) => {
        if (el && !textRefs.current.includes(el)) {
            textRefs.current.push(el);
        }
    };

    useEffect(() => {
        // Only run on client-side
        if (typeof window === "undefined") return;

        // Reset all animations first
        gsap.set([carRef.current, lightsRef.current, overlayRef.current, ...textRefs.current], { clearProps: "all" });

        // Set initial states
        gsap.set(carRef.current, {
            rotationY: 180,
            opacity: 1,
            transformStyle: "preserve-3d"
        });

        gsap.set(lightsRef.current, {
            opacity: 0, // Lights start off
            transformStyle: "preserve-3d"
        });

        gsap.set(overlayRef.current, {
            opacity: 0, // Overlay starts hidden
        });

        gsap.set(textRefs.current, {
            opacity: 0,
            x: (i) => (i % 2 === 0 ? 500 : -500) // Alternate directions
        });

        // Car flip animation
        const carAnim = gsap.to(carRef.current, {
            rotationY: 0,
            opacity: 1,
            duration: 10.2,
            ease: "power3.out",
            scrollTrigger: {
                trigger: containerRef.current,
                start: "top 10%",
                end: "top 2%",
                scrub: 2,
                markers: false,
                id: "car-anim",
                onUpdate: (self) => {
                    // Hide rotateCar.png when rotationY reaches 0 (progress ≈ 1)
                    const progress = self.progress;
                    gsap.set(carRef.current, {
                        opacity: progress < 0.95 ? 1 : 0 // Hide when nearly fully rotated
                    });
                }
            }
        });

        // Lights animation: turn on when car reaches rotationY: 0, off when back to 180
        gsap.to(lightsRef.current, {
            opacity: 1, // Lights on
            duration: 0.1,
            scrollTrigger: {
                trigger: containerRef.current,
                start: "top 10%",
                end: "top 2%",
                scrub: 2,
                onUpdate: (self) => {
                    const progress = self.progress;
                    gsap.set(lightsRef.current, {
                        opacity: progress > 0.5 ? 1 : 0
                    });
                },
                id: "lights-anim"
            }
        });

        // Overlay animation: show when car is fully rotated
        gsap.to(overlayRef.current, {
            opacity: 1, // Show overlay
            duration: 0.5,
            scrollTrigger: {
                trigger: containerRef.current,
                start: "top 10%",
                end: "top 2%",
                scrub: 2,
                onUpdate: (self) => {
                    const progress = self.progress;
                    gsap.set(overlayRef.current, {
                        opacity: progress > 0.95 ? 1 : 0 // Show when nearly fully rotated
                    });
                },
                id: "overlay-anim"
            }
        });

        // Text animations with proper sequencing
        textRefs.current.forEach((textEl, index) => {
            gsap.to(textEl, {
                opacity: 1,
                x: 0,
                duration: 2.2,
                delay: index * 0.7,
                ease: "power2.out",
                scrollTrigger: {
                    trigger: containerRef.current,
                    start: "top 10%",
                    end: "top 2%",
                    scrub: 2,
                    pin: false,
                    id: `text-${index}`
                }
            });
        });

        return () => {
            ScrollTrigger.getAll().forEach(trigger => trigger.kill());
            carAnim.kill();
        };
    }, []);

    return (
        <section className="middle-banner" ref={containerRef}>
            <div className="banner-content">
                {/* Car Image with Ref */}
                <div className="carWrapper" ref={carRef}>
                    <img
                        src={flipcar}
                        alt="Premium Car"
                        className="carImages"
                    />
                    <img
                        src={carLightsOn}
                        alt="Car Lights On"
                        className="carImages lights"
                        ref={lightsRef}
                    />
                </div>
                {/* Background Overlay */}
                <div className="overlay" ref={overlayRef}></div>

                {/* Text Content with Refs */}
                <div className="text-content">
                    <div className="text-box top-left" ref={addToRefs}>
                        <h3>Reasons to Buy</h3>
                        <p>We offer high-quality used cars, machines, bikes, and trucks, ensuring the best value for your money with a focus on quality and reliability.</p>
                    </div>

                    <div className="text-box bottom-left" ref={addToRefs}>
                        <h3>Get your car, where-ever you are</h3>
                        <p>With around multiple offices we can ship cars to Africa, Asia Caribbean, Oceania, Middle East, under continuous monitoring by our experienced staff.</p>
                    </div>

                    <div className="text-box top-right" ref={addToRefs}>
                        <h3>Reliability is our identity</h3>
                        <p>Since a quarter of Century, M.I Japan is renowned for quick delivery and reliability of service.</p>
                    </div>

                    <div className="text-box bottom-right" ref={addToRefs}>
                        <h3>Customer support 24/7</h3>
                        <p>Our dynamic team is on call around the clock to answer any query about shipped car via SKC JAPAN LiveChat, WhatsApp, Social Media, phones and emails.</p>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default MiddleBanner;